import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { signupClientUserAPI } from "../services/authService";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import "./styles.css";

function SignUp() {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  const primary = p.get("primary");
  const secondary = p.get("secondary");
  const bgHex = p.get("bg");
  const bgImageRaw = p.get("bg_image");
  const redirect_url = p.get("redirect_url");
  const incomingTitle = p.get("title");
  const faviconParam = p.get("favicon");

  const queryUrl = `redirect_url=${encodeURIComponent(
    redirect_url || ""
  )}&primary=${encodeURIComponent(primary || "")}&secondary=${encodeURIComponent(
    secondary || ""
  )}&bg=${encodeURIComponent(bgHex || "")}&bg_image=${encodeURIComponent(
    bgImageRaw || ""
  )}&title=${encodeURIComponent(incomingTitle || "")}&favicon=${encodeURIComponent(
    faviconParam || ""
  )}`;

  const passwordPattern =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$@!%&*?])[A-Za-z\d#$@!%&*?]{8,30}$/;

  const navigate = useNavigate();

  // Names
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");

  // Phone (stored separately) — default US
  const [phoneCountryIso, setPhoneCountryIso] = useState("us");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState(""); // national digits only

  // Auth
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm_password, setConfirmPassword] = useState("");

  // UI
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  // Errors
  const [firstNameErrors, setFirstNameErrors] = useState("");
  const [lastNameErrors, setLastNameErrors] = useState("");
  const [mobileErrors, setMobileErrors] = useState("");
  const [emailValidationErrors, setEmailValidationErrors] = useState("");
  const [passwordValidationErrors, setPasswordValidationErrors] = useState("");
  const [confirmPasswordValidationErrors, setConfirmPasswordValidationErrors] =
    useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // ---------- Validators ----------
  const validateName = (v) => {
    if (!v?.trim()) return "This field is required";
    if (v.length > 60) return "Maximum 60 characters";
    return "";
  };

  const validatePhone = (dial, national) => {
    const cleanedNational = String(national || "").replace(/\D/g, "");
    if (!cleanedNational) return "Mobile number is required";

    const e164 = `${dial}${cleanedNational}`.replace(/^\+?/, "+");
    const parsed = parsePhoneNumberFromString(e164);
    if (!parsed) return "Enter a valid phone number";
    if (!parsed.isPossible()) return "Number length not valid for country";
    if (!parsed.isValid()) return "Enter a valid phone number";

    return "";
  };

  const validatePassword = (pwd) => {
    if (!pwd) return "Password is required";
    if (!passwordPattern.test(pwd)) {
      return "Must include A–Z, a–z, 0–9 and one symbol (#$@!%&*?). Length 8–30.";
    }
    return "";
  };

  const validateConfirm = (pwd, cpwd) => {
    if (!cpwd) return "Confirm password is required";
    if (pwd !== cpwd) return "Passwords must match";
    return "";
  };

  // ---------- Handlers ----------
  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    setPasswordValidationErrors(validatePassword(val));
    if (confirm_password) {
      setConfirmPasswordValidationErrors(validateConfirm(val, confirm_password));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setConfirmPasswordValidationErrors(validateConfirm(password, val));
  };

  const handlePhoneChange = (val, country) => {
    const rawDigits = String(val || "").replace(/\D/g, "");
    const e164 = `+${rawDigits}`;
    const parsed = parsePhoneNumberFromString(e164);

    const iso = (parsed?.country || country?.iso2 || country?.countryCode || "us").toLowerCase();
    const dial = `+${parsed?.countryCallingCode || country?.dialCode || "1"}`;
    const national = (parsed?.nationalNumber?.toString() ||
      rawDigits.replace(new RegExp(`^${country?.dialCode || ""}`), "")).replace(/\D/g, "");

    setPhoneCountryIso(iso);
    setPhoneCountryCode(dial);
    setPhoneNumber(national);

    setMobileErrors(validatePhone(dial, national));
  };

  // Helps if some values arrive quoted (e.g., "\"foo\"")
  const unquote = (v) =>
    typeof v === "string" && /^".*"$/.test(v) ? v.slice(1, -1) : v;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setFirstNameErrors("");
    setLastNameErrors("");
    setMobileErrors("");
    setEmailValidationErrors("");
    setPasswordValidationErrors("");
    setConfirmPasswordValidationErrors("");

    const fErr = validateName(first_name);
    const lErr = validateName(last_name);
    const mErr = validatePhone(phoneCountryCode, phoneNumber);
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailOk) setEmailValidationErrors("Please enter a valid email.");
    const pErr = validatePassword(password);
    const cErr = validateConfirm(password, confirm_password);

    if (fErr || lErr || mErr || !emailOk || pErr || cErr) {
      setFirstNameErrors(fErr);
      setLastNameErrors(lErr);
      setMobileErrors(mErr);
      setPasswordValidationErrors(pErr);
      setConfirmPasswordValidationErrors(cErr);
      return;
    }

    try {
      setShowLoading(true);

      // ✅ Backend expects only these fields
      const payload = {
        first_name: unquote(first_name.trim()),
        last_name: unquote(last_name.trim()),
        email: unquote(email.trim().toLowerCase()),
        password: unquote(password),
        phone_country_code: unquote(phoneCountryCode), // "+1", "+91", ...
        phone_number: unquote(phoneNumber),            // national digits
        redirect_url: unquote(redirect_url || ""),
      };

      const res = await signupClientUserAPI(payload);
      const responseData = await res.json().catch(() => ({}));
      setShowLoading(false);

      if (res.status === 200 || res.status === 201) {
        localStorage.setItem("signin-details", JSON.stringify(responseData));
        navigate(
          "/signin/client?" + queryUrl + `&email=${encodeURIComponent(email)}`
        );
      } else if (res.status === 422) {
        // Handle errors that may be array of objects OR array of strings
        const errs = Array.isArray(responseData.errors) ? responseData.errors : [];
        if (errs.length) {
          // Object form: [{ property, message }]
          errs.forEach((e) => {
            if (typeof e === "string") return; // will be handled below
            const prop = e.property || "";
            const msg = e.message || "";
            if (prop === "first_name") setFirstNameErrors(msg);
            else if (prop === "last_name") setLastNameErrors(msg);
            else if (prop === "email") setEmailValidationErrors(msg);
            else if (prop === "password") setPasswordValidationErrors(msg);
            else if (prop === "phone_country_code" || prop === "phone_number")
              setMobileErrors(msg);
          });

          // String array form: show first error globally if field mapping didn't catch it
          const firstStringErr = errs.find((x) => typeof x === "string");
          if (firstStringErr && !errorMessage) setErrorMessage(firstStringErr);
        } else {
          setErrorMessage(responseData.message || "Validation failed.");
        }
      } else {
        setErrorMessage(responseData.message || "Sign up failed.");
      }
    } catch (err) {
      setShowLoading(false);
      setErrorMessage("Something went wrong. Please try again.");
    }
  };

  const canSubmit =
    !showLoading &&
    first_name.trim() &&
    last_name.trim() &&
    phoneCountryCode &&
    phoneNumber &&
    email &&
    password &&
    confirm_password &&
    !firstNameErrors &&
    !lastNameErrors &&
    !mobileErrors &&
    !emailValidationErrors &&
    !passwordValidationErrors &&
    !confirmPasswordValidationErrors;

  return (
    <>
      <h2>Sign Up</h2>

      <form onSubmit={handleSubmit} noValidate>
        {/* First Name */}
        <div className="inputbox relative border-b-2 border-white field-input bg-transparent">
          <input
            type="text"
            placeholder="First Name *"
            className="input w-full max-w-sm"
            value={first_name}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            required
            aria-required="true"
          />
          <p className="text-[#ff3333] mb-2 space-x-4">{firstNameErrors}</p>
        </div>

        {/* Last Name */}
        <div className="inputbox relative border-b-2 border-white field-input bg-transparent">
          <input
            type="text"
            placeholder="Last Name *"
            className="input w-full max-w-sm"
            value={last_name}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            required
            aria-required="true"
          />
          <p className="text-[#ff3333] mb-2 space-x-4">{lastNameErrors}</p>
        </div>

        {/* Email */}
        <div className="inputbox relative border-b-2 border-white field-input bg-transparent">
          <input
            type="text"
            placeholder="Email ID *"
            className="input w-full max-w-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            aria-required="true"
          />
          <p className="text-[#ff3333] mb-2 space-x-4">{emailValidationErrors}</p>
        </div>

        {/* Mobile (searchable with flags & dial codes) */}
        <div className="inputbox relative border-b-2 border-white field-input bg-transparent max-w-sm">
          <PhoneInput
            country={phoneCountryIso} // "us" default
            value={`${phoneCountryCode.replace("+", "")}${phoneNumber}`}
            onChange={handlePhoneChange}
            placeholder="Mobile number *"
            enableSearch
            preferredCountries={["us", "in", "ae", "gb"]}
            searchPlaceholder="Search country"
            inputProps={{
              name: "phone",
              required: true,
              autoComplete: "tel",
              "aria-required": "true",
            }}
            containerStyle={{ width: "100%" }}
            inputStyle={{ width: "100%", background: "transparent", color: "inherit" }}
            dropdownStyle={{ background: "#ffffff", color: "#111111" }}
            searchStyle={{ background: "#ffffff", color: "#111111" }}
          />
          <p className="text-[#ff3333] mb-2 space-x-4">{mobileErrors}</p>
        </div>

        {/* Password */}
        <div className="inputbox relative border-b-2 border-white">
          <div className="relative flex items-center">
            <input
              type={showPwd ? "text" : "password"}
              placeholder="Password *"
              onChange={handlePasswordChange}
              value={password}
              className="input w-full max-w-sm bg-transparent focus:outline-none pr-10"
              autoComplete="new-password"
              aria-invalid={!!passwordValidationErrors}
              required
              aria-required="true"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPwd((s) => !s)}
              aria-label={showPwd ? "Hide password" : "Show password"}
              title={showPwd ? "Hide password" : "Show password"}
            >
              {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
          <p className="text-[#ff3333] mb-2 space-x-4">{passwordValidationErrors}</p>
        </div>

        {/* Confirm Password */}
        <div className="inputbox relative border-b-2 border-white">
          <div className="relative flex items-center">
            <input
              type={showConfirm ? "text" : "password"}
              placeholder="Confirm Password *"
              onChange={handleConfirmPasswordChange}
              value={confirm_password}
              className="input w-full max-w-sm bg-transparent focus:outline-none pr-10"
              autoComplete="new-password"
              aria-invalid={!!confirmPasswordValidationErrors}
              required
              aria-required="true"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white "
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowConfirm((s) => !s)}
              aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
              title={showConfirm ? "Hide" : "Show"}
            >
              {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
          <p className="text-[#ff3333] mb-2 space-x-4">
            {confirmPasswordValidationErrors}
          </p>
        </div>

        {/* Global error */}
        <p className="text-[#ff3333] mb-2 space-x-4">{errorMessage}</p>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full rounded-full py-2 font-semibold text-white duration-300 button-background disabled:opacity-60"
        >
          {showLoading ? "Signing up..." : "Sign up"}
        </button>

        <div className="w-full flex justify-end mt-2 pb-5 pt-2">
          <span className="pl-10 text-white">
            Already have an account?&nbsp;&nbsp;
            <Link className="link text-white" to={"/signin/client?" + queryUrl}>
              Login Here
            </Link>
          </span>
        </div>
      </form>
    </>
  );
}

export default SignUp;
